import { toast } from "react-toastify";
import { APP_CONFIG } from "../../../constants/constants";
import i18n from "../../../services/i18n";
import { ColorRGB } from "../../../types/common";
import {
  TGeozoneMapData,
  TGeozoneMapDataCircle,
  TLatLng,
} from "../../../types/map";
import { basicDebounce } from "../../../utils/common";
import {
  getDistanceBetweenTwoLocations,
  renderCircle,
} from "../../../utils/map";
import { mapInfoBoxContent } from "../../map-fleet/common";
import { TAssetLocationDataPoint } from "../../map-fleet/type";
import { TMapOperations, TMapUpdatesHandler } from "./type";

const dragLabelCenter = {
  // subTitle: 'to reposition',
  // title: 'Drag',
};
const dragLabelRadius = {
  // subTitle: 'to resize',
  // title: 'Drag',
};

export const mapOperations: TMapOperations = ({
  drawShapeOnly = false,
  ...props
}) => {
  const google = (window as any).google;
  (window as any).map = props.mapRef.current;

  // create a namespace for map objects
  props.mapRef.current.objects = {
    mPushpins: {
      pCentre: null,
      pRadius: null,
    },
    mCircle: null,
    circleRadius:
      (props.mapData as TGeozoneMapDataCircle).radius ??
      APP_CONFIG.MAPS.DEFAULTS.RADIUS,
    mRadiusLine: null,
  };

  const renderMapObjects = async () => {
    const { AdvancedMarkerElement, PinElement } =
      await google.maps.importLibrary("marker");
    const { spherical } = await google.maps.importLibrary("geometry");
    const color = (props.mapData.color ??
      APP_CONFIG.MAPS.DEFAULTS.ZONE_COLOR) as ColorRGB;
    if (
      !(props.mapData as TGeozoneMapDataCircle).centerPosition.latitude ||
      !(props.mapData as TGeozoneMapDataCircle).centerPosition.longitude
    ) {
      if (APP_CONFIG.DEBUG.GEOZONES)
        console.error(
          `Invalid center position, cannot render the circle for geozone '${props.geozoneId}'.`
        );
      return;
    }
    let refCenter: google.maps.LatLng = new google.maps.LatLng(
      (props.mapData as TGeozoneMapDataCircle).centerPosition.latitude,
      (props.mapData as TGeozoneMapDataCircle).centerPosition.longitude
    );
    if (APP_CONFIG.DEBUG.GEOZONES)
      console.log("Dropping center at", refCenter.lat, refCenter.lng);
    // create center marker and pan the map to the geozone
    if (!drawShapeOnly) {
      // set the view to the center point
      props.mapRef.current.map.setOptions({ center: refCenter });
      // create center marker
      const pinBackground = new PinElement({
        background: "#5959ff", // specific blue shade
        borderColor: "#3434ad", // dark blue shade
        glyphColor: "#3434ad", // dark blue shade // "#f3f4f6", // grey
      });
      props.mapRef.current.objects.mPushpins.pCentre =
        new AdvancedMarkerElement({
          position: refCenter,
          map: props.mapData.editable ? props.mapRef.current.map : null,
          gmpDraggable: true,
          ...(props.mapData.editable ? dragLabelCenter : {}),
          content: pinBackground.element,
        });
      props.mapRef.current.objects.mPushpins.pCentre.setMap(
        props.mapData.editable ? props.mapRef.current.map : null
      );
    }

    // render the circle
    const circleLayer = renderCircle(
      props.mapRef,
      refCenter,
      props.mapRef.current.objects.circleRadius,
      color,
      drawShapeOnly
    );
    // add an infobox to the center of the shape, that shows on hover
    if (drawShapeOnly && !!props.geozoneId) {
      const infobox = new google.maps.InfoWindow({
        content: mapInfoBoxContent(
          { deviceid: props.geozoneId } as TAssetLocationDataPoint,
          true
        ),
        position: refCenter, // used to identify position of the infobox
        headerDisabled: true,
        zIndex: APP_CONFIG.MAPS.INFOBOX_ZINDEX,
      });
      const mouseoverHandler = (e: any) => {
        const currentZoom = props.mapRef.current.map.getZoom();
        infobox.setOptions({
          disableAutoPan: currentZoom >= APP_CONFIG.MAPS.ZOOM_MAX - 2,
        });
        infobox.setPosition(
          props.mapRef.current.map.getBounds().contains(refCenter) &&
            currentZoom < APP_CONFIG.MAPS.ZOOM_MAX - 2
            ? refCenter // center of the geozone bounds
            : true
              ? new google.maps.LatLng(e.latLng.lat(), e.latLng.lng()) // position of the cursor
              : props.mapRef.current.map.getCenter() // map viewport center
        );
        infobox.open({
          // anchor: circleLayer,
          map: props.mapRef.current.map,
        });
        // Close info box after 4 sec to let user interact with other markers
        setTimeout(() => {
          infobox.close();
        }, 4000);
      };
      const debouncedMouseoverHandler = basicDebounce(
        mouseoverHandler,
        1000 * 1
      );
      if (!!circleLayer) {
        google.maps.event.addListener(
          circleLayer,
          "mouseover",
          debouncedMouseoverHandler
        );
        google.maps.event.addListener(circleLayer, "mouseout", (e: any) => {
          infobox.close();
        });
      }
    }

    if (!circleLayer) return;

    if (drawShapeOnly) return;

    const centerPushpin = props.mapRef.current.objects.mPushpins.pCentre;

    // add event listener for dragstart event on the edge pushpin
    let originalLocation: any = null;
    // Microsoft.Maps.Events.addHandler(newPushpin, "dragstart", (args: any) => {
    //   originalLocation = newPushpin.getLocation();
    // });
    google.maps.event.addListener(centerPushpin, "drag", (args: any) => {
      if (originalLocation === null) originalLocation = centerPushpin.position;
    });

    // add event listener for dragend event on the center pushpin
    google.maps.event.addListener(centerPushpin, "dragend", (args: any) => {
      try {
        // move the circle
        circleLayer.setCenter(args.latLng);
        // fit the map to the circle
        const bounds = circleLayer.getBounds();
        props.mapRef.current.map?.fitBounds(bounds, 20);

        // store new location in pCenter
        props.mapRef.current.objects.mPushpins.pCentre.position = args.latLng;
        // props.mapRef.current.objects.mPushpins.pCentre.setPosition(args.latLng);

        // update the map data
        console.log("[setMapData] via mapOperations (center dragend)");
        props.setMapData({
          ...props.mapData,
          centerPosition: {
            latitude: args.latLng.lat(),
            longitude: args.latLng.lng(),
          },
          locs: [
            {
              latitude: args.latLng.lat(),
              longitude: args.latLng.lng(),
            },
          ],
          radius: props.mapRef.current.objects.circleRadius,
        } as TGeozoneMapData);

        // new radius point
        const radiusPosition = spherical.computeOffset(
          props.mapRef.current.objects.mPushpins.pCentre.position, // from
          props.mapRef.current.objects.circleRadius, // distance
          45 // angle
        );

        // update radius line
        props.mapRef.current.objects.mRadiusLine.setPath([
          props.mapRef.current.objects.mPushpins.pCentre.position,
          radiusPosition,
        ]);

        // move the radius marker
        radiusMarker.position = radiusPosition;
      } catch (error) {
        console.error(error);
        toast.error(
          i18n.t("admins.geozones.detailsPage.map.polygonError.dragend")
        );
        // Reset the pushpin to its original location
        centerPushpin.position = new google.maps.LatLng(
          props.mapData.centerPosition?.latitude,
          props.mapData.centerPosition?.longitude
        );
        circleLayer.setCenter(
          new google.maps.LatLng(
            props.mapData.centerPosition?.latitude,
            props.mapData.centerPosition?.longitude
          )
        );
        // fit the map to the circle
        const bounds = circleLayer.getBounds();
        props.mapRef.current.map?.fitBounds(bounds, 20);
      }
    });

    // create radius pushpin
    const radiusPosition = spherical.computeOffset(
      refCenter, // from
      props.mapRef.current.objects.circleRadius, // distance
      45 // angle
    );

    // const radiusPushpinSvg = parser.parseFromString(
    //   svgIcons.MapMarkerRed.toString(),
    //   "image/svg+xml"
    // ).documentElement;
    const bgEdgePin = new PinElement({
      background: "#ea4336", // specific red shade
      borderColor: "#b31512", // dark red shade
      glyphColor: "#b31512", // dark red shade
    });
    const radiusMarker = new AdvancedMarkerElement({
      map: props.mapData.editable ? props.mapRef.current.map : null,
      position: radiusPosition,
      title: "",
      gmpDraggable: true,
      content: bgEdgePin.element,
    });
    props.mapRef.current.objects.mPushpins.pRadius = radiusMarker;

    // create radius line
    const lineSymbol = {
      path: "M 0,-1 0,1",
      strokeColor: `rgb(${color.join(",")})`, // color,
      strokeOpacity: 1,
      scale: 3,
    };
    props.mapRef.current.objects.mRadiusLine = new google.maps.Polyline({
      path: [
        props.mapRef.current.objects.mPushpins.pCentre.position,
        props.mapRef.current.objects.mPushpins.pRadius.position,
      ],
      strokeOpacity: 0,
      icons: [
        {
          icon: lineSymbol,
          offset: "0",
          repeat: "20px",
        },
      ],
      map: props.mapData.editable ? props.mapRef.current.map : null,
    });

    google.maps.event.addListener(radiusMarker, "dragend", (args: any) => {
      try {
        // redraw the circle
        const newLocation = {
          latitude: radiusMarker.position.lat,
          longitude: radiusMarker.position.lng,
        } as TLatLng;
        const distanceFromCenter = getDistanceBetweenTwoLocations(
          {
            latitude:
              props.mapRef.current.objects.mPushpins.pCentre.position.lat,
            longitude:
              props.mapRef.current.objects.mPushpins.pCentre.position.lng,
          } as TLatLng,
          newLocation
        );
        props.mapRef.current.objects.circleRadius = distanceFromCenter; // update the circle radius
        // props.mapRef.current.objects.mPushpins.pRadius.setPosition(distanceFromCenter);
        // renderCircle(props.mapRef, refCenter, distanceFromCenter, color);
        // update the map data
        console.log("[setMapData] via mapOperations (radius dragend)");
        props.setMapData({
          ...props.mapData,
          centerPosition: {
            latitude:
              props.mapRef.current.objects.mPushpins.pCentre.position.lat,
            longitude:
              props.mapRef.current.objects.mPushpins.pCentre.position.lng,
          },
          locs: [
            {
              latitude:
                props.mapRef.current.objects.mPushpins.pCentre.position.lat,
              longitude:
                props.mapRef.current.objects.mPushpins.pCentre.position.lng,
            },
          ],
          radius: props.mapRef.current.objects.circleRadius,
        } as TGeozoneMapData);
        // update circle
        circleLayer.setRadius(props.mapRef.current.objects.circleRadius);
        // fit the map to the circle
        const bounds = circleLayer.getBounds();
        props.mapRef.current.map?.fitBounds(bounds, 20);
        // update radius line
        props.mapRef.current.objects.mRadiusLine.setPath([
          props.mapRef.current.objects.mPushpins.pCentre.position,
          radiusMarker.position,
        ]);
      } catch (error) {
        console.error(error);
        toast.error(
          i18n.t("admins.geozones.detailsPage.map.polygonError.dragend")
        );
        // Reset the pushpin to its original location
        // newPushpin.setLocation(originalLocation);
        // newPushpin.position = originalLocation;
        // const polygonPointLocations =
        //   props.mapRef.current.objects.mPushpins.pPoints.map(
        //     (point: any) => point.position
        //   );
        renderCircle(
          props.mapRef,
          originalLocation,
          props.mapRef.current.objects.circleRadius,
          color
        );
        // renderPolyline(props.mapRef, refCenter, newLocation, color, false);
      }
    });
  };

  renderMapObjects();

  return null;
};

export const mapUpdatesHandler: TMapUpdatesHandler = (props, action, value) => {
  if (APP_CONFIG.DEBUG.GEOZONES)
    console.log("[mapUpdatesHandler]", action, value);
  switch (action) {
    case "edit":
      props.mapRef.current.objects?.mPushpins?.pCentre?.setMap(
        props.mapRef.current.map
      );
      props.mapRef.current.objects?.mPushpins?.pRadius?.setMap(
        props.mapRef.current.map
      );
      props.mapRef.current.objects?.mRadiusLine?.setMap(
        props.mapRef.current.map
      );
      break;
    default:
      break;
  }
};
